<template>
  <div v-show="shouldShowAd" class="ads-item" :class="{ 'ads-loading': isLoading }">
    <div v-show="isAdFilled" class="ads-borded">
      <div class="ads-borded-title">
        Advertisement
      </div>
      <!-- AdX/GPT Implementation -->
      <div :id="adxId" ref="ads" :style="adxStyle" :data-ad-slot="ads?.path || ads?.id" class="gpt-ad"></div>
    </div>
    <div v-show="showDebug" class="ad-msg">
      <div>Status: {{ adStatus }}</div>
      <div>Load Time: {{ loadTime }}ms</div>
      <div>{{ ads }}</div>
    </div>
  </div>
</template>

<script>
const AD_STATUS = {
  PENDING: 'pending',
  FILLED: 'filled',
  UNFILLED: 'unfilled',
  ERROR: 'error'
}

export default {
  props: {
    ads: {
      type: [String, Object],
      required: false,
      default: null
    },
    showDebug: {
      type: Boolean,
      required: true
    },
    timeout: {
      type: Number,
      default: 5000
    }
  },

  data () {
    return {
      isAdFilled: true,
      isLoading: true,
      adStatus: AD_STATUS.PENDING,
      loadTime: 0,
      loadStartTime: null,
      timeoutTimer: null,
      errorCount: 0,
      maxErrors: 3,
      renderTimeout: null,
      shouldShowAd: true
    }
  },

  computed: {
    adxId () {
      if (this.ads && this.ads.id) return this.ads.id
      // Fallback if no ID provided in config
      return `div-gpt-ad-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    },
    adxStyle () {
      return this.ads && this.ads.style ? this.ads.style : 'min-width: 300px; min-height: 250px;'
    }
  },

  watch: {
    ads: {
      immediate: true,
      handler (newAds) {
        if (newAds) {
          this.resetState()
          this.initAd()
        }
      }
    }
  },

  mounted () {
    this.ads && this.initAd()
    // 通知父组件重新观察广告（如果父组件使用了adMixin）
    this.$parent.observeAds && this.$parent.observeAds()
  },

  beforeDestroy () {
    this.clearTimers()
  },

  methods: {
    initAd () {
      this.loadStartTime = Date.now()
      this.observeAdStatus()
    },

    observeAdStatus () {
      const ads = this.$refs.ads
      if (!ads) return

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'data-ad-status') {
            this.updateAdStatus(mutation.target.getAttribute('data-ad-status'))
          }
        })
      })

      observer.observe(ads, {
        attributes: true,
        attributeFilter: ['data-ad-status']
      })

      this.updateAdStatus(ads.getAttribute('data-ad-status'))
    },

    updateAdStatus (status) {
      this.adStatus = status || AD_STATUS.PENDING
      this.isAdFilled = status !== AD_STATUS.UNFILLED && status !== AD_STATUS.ERROR
      this.isLoading = status !== AD_STATUS.FILLED && status !== AD_STATUS.ERROR

      if (status === AD_STATUS.FILLED) {
        this.loadTime = Date.now() - this.loadStartTime
        this.clearTimeout()
        this.$emit('adLoaded', {
          status,
          loadTime: this.loadTime
        })
      }
    },

    handleAdError (error) {
      this.errorCount++

      if (process.env.NODE_ENV === 'development') {
        console.warn(`Ad load attempt ${this.errorCount}/${this.maxErrors}: ${error.message}`)
      }

      this.$eventrack?.('ad_load_error', 'error', {
        errorType: error.message,
        attempt: this.errorCount,
        adSlot: this.ads?.['data-ad-slot']
      })

      if (this.errorCount < this.maxErrors) {
        this.retryAd()
      } else {
        this.adStatus = AD_STATUS.ERROR
        this.isAdFilled = false
        this.isLoading = false
        this.shouldShowAd = false
        this.$emit('adError', {
          error: error.message,
          attempts: this.errorCount,
          adSlot: this.ads?.['data-ad-slot']
        })
      }
    },

    retryAd () {
      this.clearTimers()
      this.renderTimeout = setTimeout(() => {
        this.initAd()
      }, 1000 * this.errorCount)
    },

    clearTimeout () {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer)
        this.timeoutTimer = null
      }
    },

    resetState () {
      Object.assign(this.$data, {
        isAdFilled: true,
        isLoading: true,
        adStatus: AD_STATUS.PENDING,
        loadTime: 0,
        errorCount: 0,
        shouldShowAd: true
      })
      this.clearTimers()
    },

    clearTimers () {
      [this.timeoutTimer, this.renderTimeout].forEach(timer => {
        timer && clearTimeout(timer)
      })
      this.timeoutTimer = this.renderTimeout = null
    }
  }
}
</script>

<style scoped>
.ads-loading {
	min-height: 100px;
}

.ads-item {
	position: relative;
	transition: opacity 0.3s;

	&.ads-loading {
		opacity: 0.6;
	}
}

.ad-msg {
	position: absolute;
	bottom: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.7);
	color: white;
	padding: 4px 8px;
	font-size: 12px;
	border-radius: 4px;
}
</style>
